<template>
  <section class="px-3 pb-3">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">Свойства</span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadProperties">
            <i class="bi bi-arrow-repeat me-1"></i>
            Обновить
          </button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="isLoading" @click="resetForm">
            <i class="bi bi-plus-lg me-1"></i>
            Новый
          </button>
        </div>
      </div>

      <div class="row g-2 mt-1">
        <div class="col-12 col-lg-6">
          <input
            v-model.trim="searchQuery"
            type="search"
            class="form-control form-control-sm"
            placeholder="Поиск по названию, параметру, значению"
          />
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2 px-3 mt-3 mb-0">{{ successMessage }}</div>

      <div class="row g-3 mt-1">
        <div class="col-12 col-xxl-7">
          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Название</th>
                  <th>Доп. название</th>
                  <th>Параметр</th>
                  <th class="text-end">Действия</th>
                </tr>
              </thead>
              <tbody v-if="filteredProperties.length">
                <tr v-for="property in filteredProperties" :key="property.id" :class="{ 'admin-table__row--active': form.id === property.id }">
                  <td>{{ property.id }}</td>
                  <td>{{ property.label || '—' }}</td>
                  <td>{{ property.label2 || '—' }}</td>
                  <td>{{ property.param || '—' }}</td>
                  <td class="text-end">
                    <div class="btn-group btn-group-sm">
                      <button class="btn btn-outline-primary" @click="editProperty(property)">Изменить</button>
                      <button class="btn btn-outline-danger" @click="deletePropertyRow(property)">Удалить</button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="5" class="text-center text-muted py-3">Свойства не найдены.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-12 col-xxl-5">
          <div class="admin-detail">
            <div class="admin-detail__title">{{ form.id ? `Редактирование #${form.id}` : 'Создание свойства' }}</div>
            <small class="text-muted">Обязательные поля: Название, Параметр, Значение</small>

            <form class="mt-2" @submit.prevent="submitForm">
              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label mb-1">Название *</label>
                  <input v-model.trim="form.label" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Доп. название</label>
                  <input v-model.trim="form.label2" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Параметр *</label>
                  <input v-model.trim="form.param" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Значение *</label>
                  <textarea v-model="form.value" rows="4" class="form-control form-control-sm"></textarea>
                </div>
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetForm">Очистить</button>
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">
                  {{ form.id ? 'Сохранить' : 'Создать' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import { validatePropertiesUpsertRequest } from '@/admin/types/adminContracts'
import { createAdminProperty, deleteAdminProperty, getAdminProperties, updateAdminProperty } from '@/admin/api/adminService'
import { normalizeSearchText } from '@/admin/utils/adminApiUtils'

const properties = ref([])
const searchQuery = ref('')

const form = reactive({
  id: null,
  label: '',
  label2: '',
  param: '',
  value: '',
})

const {
  isLoading,
  errorMessage,
  successMessage,
  toasts,
  removeToast,
  clearMessages,
  pushError,
  execute,
} = useAdminRequestState()

const filteredProperties = computed(() => {
  const query = normalizeSearchText(searchQuery.value)
  return properties.value.filter(item => {
    if (!query) return true
    const haystack = `${item.id} ${item.label} ${item.label2 || ''} ${item.param} ${item.value}`.toLowerCase()
    return haystack.includes(query)
  })
})

const setForm = property => {
  form.id = property?.id || null
  form.label = property?.label || ''
  form.label2 = property?.label2 || ''
  form.param = property?.param || ''
  form.value = property?.value || ''
}

const resetForm = () => {
  setForm(null)
}

const editProperty = property => {
  setForm(property)
}

const loadProperties = async () => {
  clearMessages()
  const list = await execute(() => getAdminProperties(), {
    error: 'Не удалось получить список свойств.',
  })
  properties.value = list
}

const submitForm = async () => {
  clearMessages()
  const validationErrors = validatePropertiesUpsertRequest(form)
  if (validationErrors.length) {
    pushError(validationErrors[0])
    return
  }

  if (form.id) {
    await execute(
      () =>
        updateAdminProperty(form.id, {
          label: form.label,
          label2: form.label2,
          param: form.param,
          value: form.value,
        }),
      {
        success: 'Свойство обновлено.',
        error: 'Не удалось обновить свойство.',
      }
    )
  } else {
    await execute(
      () =>
        createAdminProperty({
          label: form.label,
          label2: form.label2,
          param: form.param,
          value: form.value,
        }),
      {
        success: 'Свойство создано.',
        error: 'Не удалось создать свойство.',
      }
    )
  }

  resetForm()
  await loadProperties()
}

const deletePropertyRow = async property => {
  const confirmed = window.confirm(`Удалить свойство #${property.id}?`)
  if (!confirmed) return

  clearMessages()
  await execute(() => deleteAdminProperty(property.id), {
    success: 'Свойство удалено.',
    error: 'Не удалось удалить свойство.',
  })

  if (form.id === property.id) resetForm()
  await loadProperties()
}

onMounted(async () => {
  await loadProperties()
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-table__row--active td {
  background: #eef5ff;
}

.admin-detail {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-detail__title {
  color: #1f2d45;
  font-weight: 700;
}
</style>
